/**
 * 注釈モデル（§3.2）。座標は 0-based 半開。環状ラップは `end > genomeLength`。
 */

import { Interval, Topology, resolveSpans } from '../sequence/interval';

/** 取込で受け付ける型（§3.2）。未知の型は文字列のまま保持する。 */
export const IMPORTABLE_FEATURE_TYPES = [
  'source',
  'gene',
  'CDS',
  'mRNA',
  'tRNA',
  'rRNA',
  'misc_feature',
  'regulatory',
  'promoter',
  'terminator',
  'repeat_region',
  'primer_bind',
] as const;

/** 手動で追加できる型（§3.2） */
export const EDITABLE_FEATURE_TYPES = [
  'gene',
  'CDS',
  'promoter',
  'primer_bind',
  'misc_feature',
] as const;

export type ImportableFeatureType = (typeof IMPORTABLE_FEATURE_TYPES)[number];
export type FeatureType = ImportableFeatureType | (string & {});

/** 1 = 正鎖 / -1 = 負鎖 / 0 = 鎖指定なし */
export type Strand = 1 | -1 | 0;

/** 予測 ORF の目印（永続しない。§3.2） */
export const PREDICTED_ORF_NOTE = 'predicted-orf';

export interface GenomicFeatureV1 {
  id: string;
  type: FeatureType;
  name: string;
  /** 0-based 包含 */
  start: number;
  /** 0-based 排他。環状ラップ時は `genomeLength` を超える */
  end: number;
  strand: Strand;
  /** join() 由来のエクソン。無ければ `[start, end)` 1 本と同じ */
  exons?: Interval[];
  note?: string;
  qualifiers?: Record<string, string>;
  /** 表示色の上書き（未指定なら型ごとの既定色） */
  color?: string;
}

export function isPredictedOrf(feature: GenomicFeatureV1): boolean {
  return feature.note === PREDICTED_ORF_NOTE;
}

export function featureLength(feature: GenomicFeatureV1): number {
  return Math.max(0, feature.end - feature.start);
}

/**
 * 描画・ヒット判定に使う実体スパン。join() があればそれを、無ければ本体を分子内に落とす。
 */
export function featureSpans(feature: GenomicFeatureV1, genomeLength: number): Interval[] {
  const topology: Topology = feature.end > genomeLength ? 'circular' : 'linear';
  if (feature.exons && feature.exons.length > 0) {
    return feature.exons.flatMap((exon) =>
      resolveSpans(exon, genomeLength, exon.end > genomeLength ? 'circular' : 'linear')
    );
  }
  return resolveSpans({ start: feature.start, end: feature.end }, genomeLength, topology);
}
